/**
 * useECharts — React hook for managing an ECharts instance.
 *
 * Handles:
 * - init / dispose lifecycle
 * - setOption with proper merge strategy for smooth transitions
 * - ResizeObserver for automatic chart.resize()
 * - Theme switching via dispose + re-init
 *
 * Animation strategy:
 * - First render: full setOption with notMerge=true to establish the complete
 *   chart (grid, axes, legend, tooltip, series).
 * - Subsequent data changes: partial setOption (only xAxis + yAxis + series +
 *   visualMap) with notMerge=false so ECharts matches series by index and
 *   plays smooth update transitions (bars resize, lines morph).
 *   When a filter reduces the number of series, we pad the array with empty
 *   series to overwrite stale ones — this ensures correct rendering without
 *   using replaceMerge (which would replay entry animation from scratch).
 */
import { useEffect, useRef, useCallback } from 'react';
import * as echarts from 'echarts/core';
import type { EChartsOption } from 'echarts';

// Register all required chart types and components
import {
  BarChart,
  LineChart,
  PieChart,
  ScatterChart,
  HeatmapChart,
  FunnelChart,
} from 'echarts/charts';
import {
  TitleComponent,
  TooltipComponent,
  GridComponent,
  DatasetComponent,
  LegendComponent,
  VisualMapComponent,
  MarkLineComponent,
} from 'echarts/components';
import { SVGRenderer } from 'echarts/renderers';

echarts.use([
  BarChart,
  LineChart,
  PieChart,
  ScatterChart,
  HeatmapChart,
  FunnelChart,
  TitleComponent,
  TooltipComponent,
  GridComponent,
  DatasetComponent,
  LegendComponent,
  VisualMapComponent,
  MarkLineComponent,
  SVGRenderer,
]);

export interface UseEChartsOptions {
  /** Full ECharts option object (rebuilt on every data change) */
  option: EChartsOption | null;
  /** Dark mode — triggers re-init when changed */
  isDark?: boolean;
  /** Enable animation (edit mode = true, dashboard = false) */
  animated?: boolean;
  /**
   * When true, always use notMerge=true on subsequent updates instead of
   * the partial-update path. Required for visual config changes where
   * top-level fields like `color`, `legend`, `tooltip`, `grid` must be
   * re-applied — not just xAxis/yAxis/series/visualMap.
   */
  forceFullUpdate?: boolean;
  /** Chart ID — used to emit 'echart-rendered' custom event after first render */
  chartId?: string;
}

/**
 * Extract the data-only parts of an ECharts option for partial updates.
 * Sends only xAxis, yAxis, series, and visualMap — enough for ECharts
 * to match and transition series data without rebuilding the whole chart.
 *
 * NOTE: `legend` is intentionally excluded. Re-sending legend on data
 * updates would reset the user's legend toggle state (e.g. clicking a
 * scatter category to hide it would be undone on the next data refresh).
 */
function extractDataOption(option: EChartsOption): EChartsOption {
  const partial: EChartsOption = {};

  const hasXAxis = Object.prototype.hasOwnProperty.call(option as object, 'xAxis');
  const hasYAxis = Object.prototype.hasOwnProperty.call(option as object, 'yAxis');
  const hasVisualMap = Object.prototype.hasOwnProperty.call(option as object, 'visualMap');

  // Always send xAxis / yAxis. When omitted in the new option, send an empty
  // array so that stale axes from previous chart types are removed.
  partial.xAxis = hasXAxis ? option.xAxis : [];
  partial.yAxis = hasYAxis ? option.yAxis : [];

  if (option.series) partial.series = option.series;
  // Include color array so palette changes apply via partial updates
  if ((option as any).color) partial.color = (option as any).color;
  // Include animation settings so toggle applies via partial updates
  if ('animation' in (option as any)) {
    (partial as any).animation = (option as any).animation;
    if ((option as any).animationDuration !== undefined) (partial as any).animationDuration = (option as any).animationDuration;
    if ((option as any).animationEasing !== undefined) (partial as any).animationEasing = (option as any).animationEasing;
  }

  // Likewise, ensure stale visualMap config is cleared when no longer present.
  partial.visualMap = hasVisualMap ? option.visualMap : [];

  return partial;
}

/**
 * Manages an ECharts instance on a `<div ref>`.
 *
 * @returns ref to attach to a container div
 *
 * @example
 * ```tsx
 * const chartRef = useECharts({ option, isDark, animated });
 * return <div ref={chartRef} style={{ width: '100%', height: '100%' }} />;
 * ```
 */
export function useECharts({
  option,
  isDark = false,
  animated = false,
  forceFullUpdate = false,
  chartId,
}: UseEChartsOptions) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const chartRef = useRef<echarts.ECharts | null>(null);
  const roRef = useRef<ResizeObserver | null>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  /** RAF handle for throttled chart.resize() — coalesces rapid resize events */
  const resizeRafRef = useRef<number | null>(null);
  /** Tracks whether the first full setOption has been applied */
  const hasRenderedRef = useRef(false);

  /** Create a fresh ECharts instance on the container element */
  const createChart = useCallback(
    (el: HTMLElement): echarts.ECharts => {
      const chart = echarts.init(el, isDark ? 'dark' : undefined, {
        renderer: 'svg',
      });
      // Override dark theme background to transparent (we use card bg)
      if (isDark) {
        chart.setOption({ backgroundColor: 'transparent' });
      }
      return chart;
    },
    [isDark],
  );

  // ── Init chart + ResizeObserver on mount / theme change ────────────────
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    // Dispose previous instance
    if (chartRef.current) {
      chartRef.current.dispose();
      chartRef.current = null;
    }

    hasRenderedRef.current = false;
    chartRef.current = createChart(el);

    // Auto-resize when container dimensions change.
    // Throttled via requestAnimationFrame to coalesce rapid resize events
    // during grid drag/resize — prevents expensive SVG reflows on every pixel.
    roRef.current = new ResizeObserver(() => {
      if (resizeRafRef.current !== null) cancelAnimationFrame(resizeRafRef.current);
      resizeRafRef.current = requestAnimationFrame(() => {
        resizeRafRef.current = null;
        chartRef.current?.resize();
      });
    });
    roRef.current.observe(el);

    return () => {
      roRef.current?.disconnect();
      roRef.current = null;
      if (resizeRafRef.current !== null) {
        cancelAnimationFrame(resizeRafRef.current);
        resizeRafRef.current = null;
      }
      if (timerRef.current !== null) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
      chartRef.current?.dispose();
      chartRef.current = null;
      hasRenderedRef.current = false;
    };
  }, [createChart]);

  // ── Apply option whenever data / theme changes ─────────────────────────
  useEffect(() => {
    if (!option) return;
    const el = containerRef.current;
    if (!el) return;

    // Cancel any pending deferred setOption
    if (timerRef.current !== null) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }

    // Ensure chart instance exists
    if (!chartRef.current || chartRef.current.isDisposed()) {
      chartRef.current = createChart(el);
      hasRenderedRef.current = false;
    }

    if (!hasRenderedRef.current) {
      // ── First render ──────────────────────────────────────────────────
      // Full option with notMerge=true to establish the complete chart.
      if (animated) {
        // Defer so the canvas has non-zero dimensions for entry animation.
        timerRef.current = setTimeout(() => {
          timerRef.current = null;
          if (chartRef.current && !chartRef.current.isDisposed()) {
            // Attach listener BEFORE setOption to guarantee we catch the event
            if (chartId) {
              chartRef.current.on('rendered', function onRendered() {
                chartRef.current?.off('rendered', onRendered);
                // Dispatch async so parent useEffect listeners are already attached
                setTimeout(() => window.dispatchEvent(new CustomEvent('echart-rendered', { detail: { chartId } })), 0);
              });
            }
            chartRef.current.setOption(option, { notMerge: true });
            hasRenderedRef.current = true;
          }
        }, 50);
      } else {
        // Dashboard: immediate, no animation
        // Attach listener BEFORE setOption to guarantee we catch the event
        if (chartId) {
          chartRef.current.on('rendered', function onRendered() {
            chartRef.current?.off('rendered', onRendered);
            // Dispatch async so parent useEffect listeners are already attached
            setTimeout(() => window.dispatchEvent(new CustomEvent('echart-rendered', { detail: { chartId } })), 0);
          });
        }
        chartRef.current.setOption(option, {
          notMerge: true,
          lazyUpdate: true,
        });
        hasRenderedRef.current = true;
      }
    } else if (forceFullUpdate) {
      // ── Visual config changes (edit mode) ─────────────────────────────
      // Full replace so top-level fields (color, legend, tooltip, grid)
      // are re-applied — not just data fields.
      chartRef.current.setOption(option, { notMerge: true });
    } else {
      // ── Subsequent data changes (dashboard) ───────────────────────────
      // Partial update with notMerge=false: ECharts matches series by index
      // and plays smooth update transitions (bars resize, lines morph).
      // We pad the series array with empty entries to overwrite any stale
      // series left from a previous render (e.g. when filters reduce the
      // number of scatter categories).
      const dataOption = extractDataOption(option);
      const currentOpt = chartRef.current.getOption() as any;
      const prevCount = Array.isArray(currentOpt?.series) ? currentOpt.series.length : 0;
      const newSeries = Array.isArray(dataOption.series) ? dataOption.series as any[] : [];

      if (newSeries.length < prevCount) {
        for (let i = newSeries.length; i < prevCount; i++) {
          const oldType = currentOpt.series[i]?.type ?? 'line';
          newSeries.push({ type: oldType, data: [], name: '' });
        }
        dataOption.series = newSeries;
      }

      chartRef.current.setOption(dataOption, { notMerge: false });
    }
  }, [option, animated, createChart, forceFullUpdate]);

  return containerRef;
}